import React from 'react';
import { motion } from 'framer-motion';
import { FiCreditCard, FiAlertCircle, FiCheckCircle, FiGift, FiTag, FiClipboard } from 'react-icons/fi';
import logger from '../../../utils/logging/logger';
import styles from '../PaymentForm.module.css';
import { getCourseDateRange } from './paymentFormFormatters';

// Presentational: product/product-summary list, bulk-pricing badge, promo-code
// card, and the order-summary totals. Extracted VERBATIM from PaymentForm's
// render; all dynamic values + the 3 promo handlers arrive as explicit props.
export default function OrderSummary({
  appliedPromo, applyPromoCode, bulkPricing, courses,
  error, productIds, promoCode, promoError,
  promoInfo, promoLoading, removeBulkPricing, removePromoCode,
  setPromoCode, setPromoError, setPromoInfo, totalPrice,
}) {
  return (
    <>
        {/* Product Summary Card */}
        <div className={styles.productSummaryCard}>
          <h3 className={styles.promoCardTitle}>
            <FiClipboard size={18} aria-hidden="true" />
            {productIds ? 'Product Summary' : 'Course Summary'}
          </h3>
          <div className={styles.productList}>
          {courses && courses.length > 0 ? courses.map((course) => {
            // FIX: Add null checks to prevent errors if course data is incomplete
            if (!course || !course.id) {
              logger.warn('Invalid course data in courses array', { course });
              return null;
            }
            const courseDateRange = getCourseDateRange(course);
            return (
              <div key={course.id} className={styles.productItem}>
                <div className={styles.productInfo}>
                  <h4 className={styles.productName}>{course.name || 'Unnamed Course'}</h4>
                  {!productIds && (
                    <span className={styles.productCode}>
                      {`${course.code || 'N/A'} • ${course.instructor || 'TBA'}`}
                    </span>
                  )}
                  {courseDateRange && (
                    <span className={styles.productDateRange}>{courseDateRange}</span>
                  )}
                </div>
                <span className={styles.productPrice}>
                  {course.isPaidCourse ? `$${course.price || 0}` : 'Free'}
                </span>
              </div>
            );
          }) : (
            <div className={styles.productItem}>
              <div className={styles.productInfo}>
                <h4 className={styles.productName}>No courses found</h4>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* FIX #27: Simplified bulk pricing display logic */}
        {/* FIX #27: Simplify bulk pricing display logic - check all conditions in one place */}
        {courses.length > 1 && 
         bulkPricing?.bulkPricing?.name && 
         bulkPricing?.pricing?.savings && 
         bulkPricing.pricing.savings > 0 &&
         !appliedPromo && 
         !productIds && (
          <div className={styles.bulkPricingInfo} role="status" aria-live="polite">
            <h3 className={styles.promoCardTitle}><FiGift size={13} /> Bulk discount applied</h3>
            <div className={styles.appliedPromo}>
              <div className={styles.promoInfo}>
                <span className={styles.promoCode}>{bulkPricing.bulkPricing.name}</span>
                <span className={styles.promoDiscount}>Save ${(bulkPricing.pricing.savings || 0).toFixed(2)}</span>
              </div>
              <button
                className={styles.removePromoButton}
                onClick={removeBulkPricing}
                aria-label="Remove bulk discount"
                type="button"
              >
                Remove
              </button>
            </div>
          </div>
        )}

        {/* Promo Code Card */}
        <div className={styles.promoCard}>
          <h3 className={styles.promoCardTitle}><FiTag size={13} /> Promo Code</h3>
          {appliedPromo ? (
            <div className={styles.appliedPromo} role="status" aria-live="polite">
              <div className={styles.promoInfo}>
                <span className={styles.promoCode} aria-label={`Applied promo code: ${appliedPromo.code}`}>{appliedPromo.code}</span>
                <span className={styles.promoDiscount}>
                  {appliedPromo.discount_type === 'percentage' 
                    ? `${parseFloat(appliedPromo.discount_value).toFixed(0)}% off`
                    : `$${appliedPromo.discount_value} off`
                  }
                </span>
              </div>
              <button 
                className={styles.removePromoButton}
                onClick={removePromoCode}
                aria-label={`Remove promo code ${appliedPromo.code}`}
                type="button"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className={styles.promoInput}>
              <input
                className={styles.promoCodeInput}
                id="promo-code-input"
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  if (promoError) setPromoError(null);
                  // Only clear success message if user is typing a new code (not when promo is already applied)
                  if (promoInfo && !appliedPromo) {
                    setPromoInfo(null);
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && applyPromoCode()}
                aria-label="Promo code input"
                aria-describedby={promoError ? "promo-error" : undefined}
              />
              <button 
                className={styles.promoButton}
                onClick={applyPromoCode} 
                disabled={!promoCode.trim() || promoLoading}
                aria-label="Apply promo code"
                type="button"
              >
                {promoLoading ? 'Applying...' : 'Apply'}
              </button>
            </div>
          )}
        </div>

        {promoError && (
          <motion.div
            className={styles.promoErrorMessage}
            id="promo-error" 
            role="alert" 
            aria-live="assertive"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
          >
            <div className={styles.promoErrorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.promoErrorText}>{promoError}</div>
          </motion.div>
        )}
        {promoInfo && (
          <motion.div
            className={styles.promoInfoMessage}
            id="promo-info" 
            role="status" 
            aria-live="polite"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.promoInfoIcon}>
              <FiCheckCircle />
            </div>
            <div className={styles.promoInfoText}>{promoInfo}</div>
          </motion.div>
        )}

        {/* Order Summary Card */}
        <div className={styles.orderSummaryCard} role="region" aria-label="Payment summary">
          <h3 className={styles.orderSummaryTitle}><FiCreditCard size={13} /> Order Summary</h3>
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Subtotal:</span>
            <span className={styles.totalValue} aria-label={`Subtotal: $${(courses.reduce((sum, course) => {
                const isPaid = course.isPaidCourse || course.is_paid_course;
                const price = isPaid ? (parseFloat(course.price) || 0) : 0;
                // FIX #23: Use Math.round to prevent floating point precision errors
                return Math.round((sum + price) * 100) / 100;
              }, 0) || 0).toFixed(2)}`}>
              {/* FIX #11: Use getTotalPrice() for consistent calculation (base only, no discounts) */}
              {/* FIX #23: Add precision handling to prevent floating point errors */}
              ${(courses.reduce((sum, course) => {
                const isPaid = course.isPaidCourse || course.is_paid_course;
                const price = isPaid ? (parseFloat(course.price) || 0) : 0;
                // FIX #23: Use Math.round to prevent floating point precision errors
                return Math.round((sum + price) * 100) / 100;
              }, 0) || 0).toFixed(2)}
            </span>
          </div>
          {/* FIX #6: Add null checks for bulk pricing display */}
          {bulkPricing?.pricing?.savings && !appliedPromo && (
            <div className={styles.totalRow}>
              <span className={`${styles.totalLabel} ${styles.promoAppliedLabel}`}>Bulk Discount Applied:</span>
              <span className={`${styles.totalValue} ${styles.promoDiscountValue}`}>
                -${(bulkPricing.pricing.savings || 0).toFixed(2)}
              </span>
            </div>
          )}
          {appliedPromo && (
            <div className={styles.totalRow}>
              <span className={`${styles.totalLabel} ${styles.promoAppliedLabel}`}>Promo Code Applied ({appliedPromo.code}):</span>
              <span className={`${styles.totalValue} ${styles.promoDiscountValue}`}>
                -${appliedPromo.discount_type === 'percentage' 
                  ? ((courses.reduce((sum, course) => sum + (parseFloat(course.price) || 0), 0) * (parseFloat(appliedPromo.discount_value) || 0)) / 100).toFixed(2)
                  : (parseFloat(appliedPromo.discount_value) || 0).toFixed(2)
                }
              </span>
            </div>
          )}
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total:</span>
            {/* FIX #9: Use memoized total price instead of calling function in render */}
            <span className={styles.totalValue} aria-label={`Total amount: $${(totalPrice || 0).toFixed(2)}`}>
              ${(totalPrice || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {error && (
          <motion.div
            className={styles.errorMessage}
            role="alert"
            aria-live="assertive"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              {error}
            </div>
          </motion.div>
        )}
    </>
  );
}
